import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient from '@/api/client'
import { getApiBaseUrl } from '@/api/base-url'
import { parsePrice } from '@/utils/price'
import type { CartItem } from './cart'
import type { CheckoutRequest } from '@/shared/api/generated/critical-api'

export interface OrderItem {
  id: string
  product: {
    id: string
    name: string
    sku: string
    imageUrl?: string
  }
  quantity: number
  unitPrice: number
  totalPrice: number
}

export interface Order {
  id: string | number
  order_number: string
  number?: string // для обратной совместимости
  status:
    | 'draft'
    | 'new'
    | 'pending'
    | 'confirmed'
    | 'processing'
    | 'shipped'
    | 'delivered'
    | 'cancelled'
    | 'returned'
  status_label: string
  total_amount: unknown
  final_amount: unknown
  totalAmount?: number // для обратной совместимости
  items_count: number
  itemsCount?: number // для обратной совместимости
  comment?: string
  created_at: string
  createdAt?: string // для обратной совместимости
  updated_at?: string
  updatedAt?: string // для обратной совместимости
  can_be_cancelled: boolean
  cancellation_reason?: string | null
  deleted_from_draft?: boolean
  items?: OrderItem[]
}

export interface OrderForm {
  comment: string
  deliveryMethod: 'pickup' | 'courier' | 'supplier'
  deliveryAddress?: string
  paymentMethod: 'card' | 'bank_transfer' | 'credit'
}

export interface CreateOrderData {
  items: Array<{
    productId: string
    quantity: number
    /** Поставщик и цена с НДС в копейках, которые покупатель видел при оформлении */
    confirmedSupplierId?: number
    confirmedUnitPriceWithTax?: number
  }>
  comment?: string
  deliveryMethod: string
  deliveryAddress?: string
  cityId?: number
  deliveryStreet?: string
  deliveryHouse?: string
  deliveryApartment?: string
  deliveryPostalCode?: string
  terminalId?: string
  paymentMethod: string
  customerType?: string
  customerName?: string
  customerInn?: string
  customerPhone?: string
}

interface CreateOrderPayload {
  items?: Array<{
    product_id: number
    quantity: number
    confirmed_supplier_id?: number
    confirmed_unit_price_with_tax?: number
  }>
  comment?: string
  delivery_method?: string
  delivery_address?: string
  city_id?: number
  delivery_street?: string
  delivery_house?: string
  delivery_apartment?: string
  delivery_postal_code?: string
  terminal_id?: string
  payment_method?: string
  customer_type?: string
  customer_name?: string
  customer_inn?: string
  customer_phone?: string
}

export interface DraftOrderData {
  items?: Array<{
    productId: string
    quantity: number
  }>
  comment?: string
  deliveryMethod?: string
  deliveryAddress?: string
  cityId?: number
  deliveryStreet?: string
  deliveryHouse?: string
  deliveryApartment?: string
  deliveryPostalCode?: string
  terminalId?: string
  paymentMethod?: string
  customerType?: string
  customerName?: string
  customerInn?: string
  customerPhone?: string
}

interface DraftOrderPayload {
  data: {
    items?: Array<{
      product_id: number
      quantity: number
    }>
    comment?: string
    delivery_method?: string
    delivery_address?: string
    city_id?: number
    delivery_street?: string
    delivery_house?: string
    delivery_apartment?: string
    delivery_postal_code?: string
    terminal_id?: string
    payment_method?: string
    customer_type?: string
    customer_name?: string
    customer_inn?: string
    customer_phone?: string
  }
}

/**
 * 409 idempotency_in_progress = повтори; не путать с 409 «поставщик не торгует»
 */
const IDEMPOTENCY_IN_PROGRESS_CODE = 'idempotency_in_progress'
const IDEMPOTENCY_RETRY_ATTEMPTS = 5
const IDEMPOTENCY_RETRY_DELAY_MS = 400

function isIdempotencyInProgress(e: unknown): boolean {
  if (typeof e !== 'object' || e === null || !('response' in e)) return false
  const response = (e as { response?: unknown }).response
  if (typeof response !== 'object' || response === null) return false
  const { status, data } = response as { status?: unknown; data?: unknown }
  if (status !== 409) return false
  if (typeof data !== 'object' || data === null) return false
  return (data as { code?: unknown }).code === IDEMPOTENCY_IN_PROGRESS_CODE
}

/** Машиночитаемый код отказа из `errors.code` (например, checkout_terms_changed). */
function errorCode(e: unknown): string | undefined {
  if (typeof e !== 'object' || e === null || !('response' in e)) return undefined
  const data = (e as { response?: { data?: unknown } }).response?.data
  if (typeof data !== 'object' || data === null) return undefined
  const errors = (data as { errors?: unknown }).errors
  if (typeof errors !== 'object' || errors === null) return undefined
  const code = (errors as { code?: unknown }).code
  return typeof code === 'string' ? code : undefined
}

/**
 * Двойной клик по «Отправить заказ» доходит до backend двумя запросами. Второй
 * получает 409, и без повтора пользователь увидел бы ошибку на заказе, который
 * в этот момент успешно создаётся.
 */
async function postWithIdempotencyRetry<T>(
  send: () => Promise<T>,
  delayMs: number = IDEMPOTENCY_RETRY_DELAY_MS,
): Promise<T> {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await send()
    } catch (e: unknown) {
      if (attempt >= IDEMPOTENCY_RETRY_ATTEMPTS || !isIdempotencyInProgress(e)) throw e
      await new Promise((resolve) => setTimeout(resolve, delayMs))
    }
  }
}

export const useOrderStore = defineStore('order', () => {
  // Состояние
  const orders = ref<Order[]>([])
  const currentOrder = ref<Order | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Геттеры
  const ordersCount = computed(() => orders.value.length)
  const pendingOrders = computed(() => orders.value.filter((order) => order.status === 'pending'))
  const completedOrders = computed(() =>
    orders.value.filter((order) => ['delivered', 'completed'].includes(order.status)),
  )

  // Утилита для преобразования строк в числа
  function transformOrder(order: Record<string, unknown>): Order {
    const apiBaseUrl = getApiBaseUrl()

    const parseInteger = (value: string | number | undefined, defaultValue = 0): number => {
      if (typeof value === 'number') {
        return Math.floor(value)
      }
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10)
        return isNaN(parsed) ? defaultValue : parsed
      }
      return defaultValue
    }

    const normalizeImageUrl = (value: unknown): string | undefined => {
      if (typeof value !== 'string') {
        return undefined
      }

      const normalized = value.trim()
      if (normalized === '') {
        return undefined
      }

      if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
        return normalized
      }

      if (normalized.startsWith('/')) {
        return apiBaseUrl ? `${apiBaseUrl}${normalized}` : normalized
      }

      if (normalized.startsWith('uploads/')) {
        return apiBaseUrl ? `${apiBaseUrl}/${normalized}` : `/${normalized}`
      }

      return apiBaseUrl
        ? `${apiBaseUrl}/uploads/products/images/${normalized}`
        : `/uploads/products/images/${normalized}`
    }

    return {
      ...order,
      id: String(order['id'] ?? parseInteger(order['id'] as string | number | undefined)),
      order_number: String(order['order_number'] ?? order['number'] ?? ''),
      status: String(order['status'] ?? 'new') as Order['status'],
      status_label: String(order['status_label'] ?? 'Новый'),
      total_amount: order['total_amount'] ?? 0,
      final_amount: order['final_amount'] ?? 0,
      totalAmount: parsePrice(
        order['final_amount'] ?? order['total_amount'] ?? order['totalAmount'] ?? 0,
      ),
      items_count: parseInteger(order['items_count'] as string | number | undefined, 0),
      itemsCount: parseInteger(
        (order['items_count'] as string | number | undefined) ||
          (order['itemsCount'] as string | number | undefined),
        0,
      ),
      created_at: String(order['created_at'] ?? order['createdAt'] ?? new Date().toISOString()),
      can_be_cancelled: Boolean(order['can_be_cancelled'] ?? false),
      items: Array.isArray(order['items'])
        ? (order['items'] as Array<Record<string, unknown>>).map((item, index) => {
            const productData =
              item['product'] &&
              typeof item['product'] === 'object' &&
              !Array.isArray(item['product'])
                ? (item['product'] as Record<string, unknown>)
                : undefined

            const imageUrl = normalizeImageUrl(
              productData?.['primary_image_url'] ??
                productData?.['image_url'] ??
                item['primary_image_url'] ??
                item['image_url'],
            )

            return {
              id: String(item['id'] ?? index),
              product: {
                id: String(productData?.['id'] ?? item['product_id'] ?? ''),
                name: String(productData?.['name'] ?? item['name'] ?? 'Товар'),
                sku: String(productData?.['sku'] ?? item['sku'] ?? ''),
                ...(imageUrl ? { imageUrl } : {}),
              },
              quantity: parseInteger(item['quantity'] as string | number | undefined, 0),
              unitPrice: parsePrice(
                item['unitPrice'] ?? item['unit_price'] ?? item['price_with_tax'] ?? item['price'],
              ),
              totalPrice: parsePrice(item['totalPrice'] ?? item['total_price']),
            }
          })
        : [],
    }
  }

  // Действия
  async function createOrder(orderData: CreateOrderData, idempotencyKey?: string) {
    loading.value = true
    error.value = null

    try {
      const apiUrl = getApiBaseUrl()
      const payload: CreateOrderPayload = {
        items: orderData.items.map((item) => ({
          product_id: Number(item.productId),
          quantity: item.quantity,
          ...(item.confirmedSupplierId !== undefined
            ? { confirmed_supplier_id: item.confirmedSupplierId }
            : {}),
          ...(item.confirmedUnitPriceWithTax !== undefined
            ? { confirmed_unit_price_with_tax: item.confirmedUnitPriceWithTax }
            : {}),
        })),
        delivery_method: orderData.deliveryMethod,
        payment_method: orderData.paymentMethod,
      }

      if (orderData.comment) {
        payload.comment = orderData.comment
      }
      if (orderData.deliveryAddress) {
        payload.delivery_address = orderData.deliveryAddress
      }
      if (orderData.cityId) {
        payload.city_id = orderData.cityId
      }
      if (orderData.deliveryStreet) {
        payload.delivery_street = orderData.deliveryStreet
      }
      if (orderData.deliveryHouse) {
        payload.delivery_house = orderData.deliveryHouse
      }
      if (orderData.deliveryApartment) {
        payload.delivery_apartment = orderData.deliveryApartment
      }
      if (orderData.deliveryPostalCode) {
        payload.delivery_postal_code = orderData.deliveryPostalCode
      }
      if (orderData.terminalId) {
        payload.terminal_id = orderData.terminalId
      }
      if (orderData.customerType) {
        payload.customer_type = orderData.customerType
      }
      if (orderData.customerName) {
        payload.customer_name = orderData.customerName
      }
      if (orderData.customerInn) {
        payload.customer_inn = orderData.customerInn
      }
      if (orderData.customerPhone) {
        payload.customer_phone = orderData.customerPhone
      }

      const requestConfig = idempotencyKey
        ? { headers: { 'X-Idempotency-Key': idempotencyKey } }
        : {}
      const contractPayload: CheckoutRequest = {
        items: payload.items ?? [],
      }
      if (payload.delivery_method) {
        contractPayload.delivery_method = payload.delivery_method as NonNullable<CheckoutRequest['delivery_method']>
      }
      if (payload.payment_method) {
        contractPayload.payment_method = payload.payment_method as NonNullable<CheckoutRequest['payment_method']>
      }
      const response = await postWithIdempotencyRetry(() =>
        apiClient.post(`${apiUrl}/api/orders`, { ...payload, ...contractPayload }, requestConfig),
      )

      if (response.status === 201 || response.data.success) {
        const payload = response.data.data
        const createdOrders = Array.isArray(payload?.orders)
          ? payload.orders
          : payload
            ? [payload]
            : []

        const transformedOrders = createdOrders.map(transformOrder)
        for (let i = transformedOrders.length - 1; i >= 0; i -= 1) {
          orders.value.unshift(transformedOrders[i])
        }

        currentOrder.value = transformedOrders[0] ?? null
        return {
          success: true,
          orders: createdOrders,
          orderIds: payload?.order_ids ?? [],
          orderNumbers: payload?.order_numbers ?? [],
        }
      } else {
        error.value = response.data.message || 'Ошибка создания заказа'
        return { success: false, error: error.value }
      }
    } catch (e: unknown) {
      // Повторы кончились, а первый запрос всё ещё выполняется. Техническое
      // сообщение про заголовок пользователю не поможет.
      if (isIdempotencyInProgress(e)) {
        error.value = 'Заказ уже оформляется, подождите несколько секунд'
        return { success: false, error: error.value }
      }

      const errorMessage =
        e instanceof Error &&
        'response' in e &&
        typeof e.response === 'object' &&
        e.response &&
        'data' in e.response &&
        typeof e.response.data === 'object' &&
        e.response.data &&
        'message' in e.response.data &&
        typeof e.response.data.message === 'string'
          ? e.response.data.message
          : 'Ошибка сети'

      error.value = errorMessage
      return { success: false, error: error.value, code: errorCode(e) }
    } finally {
      loading.value = false
    }
  }

  async function createDraft(orderData: DraftOrderData) {
    loading.value = true
    error.value = null

    try {
      const apiUrl = getApiBaseUrl()
      const payload: DraftOrderPayload = {
        data: {},
      }

      if (Array.isArray(orderData.items) && orderData.items.length > 0) {
        // PRO-109: тот же класс расхождения, что PRO-100 закрыл для обычного
        // checkout - product_id везде в UI строка (CartItem.productId), но wire-type
        // контракта - integer, и backend теперь это валидирует.
        payload.data.items = orderData.items.map((item) => ({
          product_id: Number(item.productId),
          quantity: item.quantity,
        }))
      }
      if (orderData.comment) {
        payload.data.comment = orderData.comment
      }
      if (orderData.deliveryMethod) {
        payload.data.delivery_method = orderData.deliveryMethod
      }
      if (orderData.deliveryAddress) {
        payload.data.delivery_address = orderData.deliveryAddress
      }
      if (orderData.cityId) {
        payload.data.city_id = orderData.cityId
      }
      if (orderData.deliveryStreet) {
        payload.data.delivery_street = orderData.deliveryStreet
      }
      if (orderData.deliveryHouse) {
        payload.data.delivery_house = orderData.deliveryHouse
      }
      if (orderData.deliveryApartment) {
        payload.data.delivery_apartment = orderData.deliveryApartment
      }
      if (orderData.deliveryPostalCode) {
        payload.data.delivery_postal_code = orderData.deliveryPostalCode
      }
      if (orderData.terminalId) {
        payload.data.terminal_id = orderData.terminalId
      }
      if (orderData.paymentMethod) {
        payload.data.payment_method = orderData.paymentMethod
      }
      if (orderData.customerType) {
        payload.data.customer_type = orderData.customerType
      }
      if (orderData.customerName) {
        payload.data.customer_name = orderData.customerName
      }
      if (orderData.customerInn) {
        payload.data.customer_inn = orderData.customerInn
      }
      if (orderData.customerPhone) {
        payload.data.customer_phone = orderData.customerPhone
      }

      const response = await apiClient.post(`${apiUrl}/api/orders/draft`, payload)

      if (response.status === 201 || response.data.success) {
        const newOrder = transformOrder(response.data.data)
        orders.value.unshift(newOrder)
        currentOrder.value = newOrder
        return { success: true, order: newOrder }
      } else {
        error.value = response.data.message || 'Ошибка сохранения черновика'
        return { success: false, error: error.value }
      }
    } catch (e: unknown) {
      const errorMessage =
        e instanceof Error &&
        'response' in e &&
        typeof e.response === 'object' &&
        e.response &&
        'data' in e.response &&
        typeof e.response.data === 'object' &&
        e.response.data &&
        'message' in e.response.data &&
        typeof e.response.data.message === 'string'
          ? e.response.data.message
          : 'Ошибка сети'

      error.value = errorMessage
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function submitDraft(
    order_id: string,
    delivery?: {
      cityId?: number
      deliveryStreet?: string
      deliveryHouse?: string
      deliveryApartment?: string
      deliveryPostalCode?: string
      terminalId?: string
    },
  ) {
    loading.value = true
    error.value = null

    try {
      const payload: Record<string, unknown> = {}
      if (delivery?.cityId) payload['city_id'] = delivery.cityId
      if (delivery?.deliveryStreet) payload['delivery_street'] = delivery.deliveryStreet
      if (delivery?.deliveryHouse) payload['delivery_house'] = delivery.deliveryHouse
      if (delivery?.deliveryApartment) payload['delivery_apartment'] = delivery.deliveryApartment
      if (delivery?.deliveryPostalCode) payload['delivery_postal_code'] = delivery.deliveryPostalCode
      if (delivery?.terminalId) payload['terminal_id'] = delivery.terminalId

      const response = await apiClient.post(`/api/orders/${order_id}/submit`, payload)
      if (response.data?.success) {
        const submitted_order = transformOrder(response.data.data)
        const order_index = orders.value.findIndex((order) => String(order.id) === order_id)
        if (order_index >= 0) {
          orders.value[order_index] = submitted_order
        } else {
          orders.value.unshift(submitted_order)
        }
        currentOrder.value = submitted_order
        return { success: true, order: submitted_order }
      }

      error.value = response.data?.message || 'Ошибка отправки заказа'
      return { success: false, error: error.value }
    } catch (e: unknown) {
      const errorMessage =
        e instanceof Error &&
        'response' in e &&
        typeof e.response === 'object' &&
        e.response &&
        'data' in e.response &&
        typeof e.response.data === 'object' &&
        e.response.data &&
        'message' in e.response.data &&
        typeof e.response.data.message === 'string'
          ? e.response.data.message
          : 'Ошибка сети'

      error.value = errorMessage
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function fetchOrders(
    page: number = 1,
    limit: number = 10,
    status?: string,
    dateFrom?: string,
    dateTo?: string,
  ) {
    loading.value = true
    error.value = null

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      })

      if (status) params.append('status', status)
      if (dateFrom) params.append('date_from', dateFrom)
      if (dateTo) params.append('date_to', dateTo)

      const response = await apiClient.get(`/api/orders?${params}`)

      if (response.data.success) {
        const transformedOrders = response.data.data.orders.map(transformOrder)
        orders.value = transformedOrders
        return {
          success: true,
          orders: transformedOrders,
          pagination: response.data.data.pagination,
        }
      } else {
        error.value = response.data.message || 'Ошибка загрузки заказов'
        return { success: false, error: error.value }
      }
    } catch (e: unknown) {
      console.error('Ошибка загрузки заказов:', e)

      const errorMessage =
        e instanceof Error &&
        'response' in e &&
        typeof e.response === 'object' &&
        e.response &&
        'data' in e.response &&
        typeof e.response.data === 'object' &&
        e.response.data &&
        'message' in e.response.data &&
        typeof e.response.data.message === 'string'
          ? e.response.data.message
          : 'Ошибка сети'

      error.value = errorMessage
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  async function fetchOrder(id: string) {
    loading.value = true
    error.value = null

    try {
      const response = await apiClient.get(`/api/orders/${id}`)

      if (response.data.success) {
        const transformedOrder = transformOrder(response.data.data)
        currentOrder.value = transformedOrder
        return { success: true, order: transformedOrder }
      } else {
        error.value = response.data.message || 'Заказ не найден'
        return { success: false, error: error.value }
      }
    } catch (e: unknown) {
      console.error('Ошибка загрузки заказа:', e)

      const errorMessage =
        e instanceof Error &&
        'response' in e &&
        typeof e.response === 'object' &&
        e.response &&
        'data' in e.response &&
        typeof e.response.data === 'object' &&
        e.response.data &&
        'message' in e.response.data &&
        typeof e.response.data.message === 'string'
          ? e.response.data.message
          : 'Ошибка сети'

      error.value = errorMessage
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  // Утилиты
  function getOrderStatusText(status: string): string {
    const statusMap: Record<string, string> = {
      draft: 'Черновик',
      new: 'Новый',
      pending: 'Ожидает подтверждения',
      confirmed: 'Подтвержден',
      processing: 'В обработке',
      shipped: 'Отправлен',
      delivered: 'Доставлен',
      cancelled: 'Отменен',
      returned: 'Возвращен',
    }
    return statusMap[status] || status
  }

  function getOrderStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      draft: 'grey',
      new: 'blue',
      pending: 'orange',
      confirmed: 'blue',
      processing: 'purple',
      shipped: 'cyan',
      delivered: 'green',
      cancelled: 'red',
      returned: 'grey',
    }
    return colorMap[status] || 'grey'
  }

  function canCancelOrder(order: Order): boolean {
    return order.can_be_cancelled || ['draft', 'new', 'pending'].includes(order.status)
  }

  function canRepeatOrder(order: Order): boolean {
    return ['delivered', 'cancelled', 'returned'].includes(order.status)
  }

  function formatOrderItems(cartItems: CartItem[]): Array<{ productId: string; quantity: number }> {
    return cartItems.map((item) => ({
      productId: String(item.productId),
      quantity: item.quantity,
    }))
  }

  function clearCurrentOrder() {
    currentOrder.value = null
  }

  async function cancelOrder(orderId: string) {
    loading.value = true
    error.value = null

    try {
      const response = await apiClient.post(`/api/orders/${orderId}/cancel`)

      if (response.data.success) {
        // Обновляем статус заказа в списке
        const orderIndex = orders.value.findIndex((order) => order.id.toString() === orderId)
        if (orderIndex !== -1) {
          const foundOrder = orders.value[orderIndex]
          if (foundOrder) {
            foundOrder.status = 'cancelled'
            foundOrder.status_label = 'Отменен'
          }
        }

        return { success: true, message: 'Заказ успешно отменен' }
      } else {
        error.value = response.data.message || 'Ошибка отмены заказа'
        return { success: false, message: error.value }
      }
    } catch (e: unknown) {
      console.error('Ошибка отмены заказа:', e)

      const errorMessage =
        e instanceof Error &&
        'response' in e &&
        typeof e.response === 'object' &&
        e.response &&
        'data' in e.response &&
        typeof e.response.data === 'object' &&
        e.response.data &&
        'message' in e.response.data &&
        typeof e.response.data.message === 'string'
          ? e.response.data.message
          : 'Ошибка сети'

      error.value = errorMessage
      return { success: false, message: error.value }
    } finally {
      loading.value = false
    }
  }

  return {
    // Состояние
    orders,
    currentOrder,
    loading,
    error,

    // Геттеры
    ordersCount,
    pendingOrders,
    completedOrders,

    // Действия
    createOrder,
    createDraft,
    submitDraft,
    fetchOrders,
    fetchOrder,
    cancelOrder,

    // Утилиты
    getOrderStatusText,
    getOrderStatusColor,
    canCancelOrder,
    canRepeatOrder,
    formatOrderItems,
    clearCurrentOrder,
  }
})
